/**
 * components/legal/vop/vopSpecification.ts
 *
 * The service specification: Annex 1 of the general terms (`vopTerms.ts`),
 * as counsel drafted it on 27 Oct 2026.
 *
 * ## Why it is held to the product
 *
 * The terms define a defect by this document ("the Service is free of
 * defects if it conforms to the Specification"), so a sentence here that the
 * product does not do is a defect from the day the terms bind. Counsel wrote
 * this draft in general words on purpose, at the owners' request not to bind
 * the company to implementation details, and where it still said what the
 * product does not do, the product is what is written down:
 *
 * - **Writes** (3.6) are as a rule only what changed: a send can carry a
 *   price the PMS already holds (the Send dialog's "send again", a minimum
 *   stay that re-sends the night's price).
 * - **The wizard** (4.9) switches the market prices on where the user says so
 *   in it.
 * - **Other users' establishments** (5.6) are not left out of the sample. The
 *   draft said they were while automatic sending is on.
 *
 * Everything else is counsel's wording, the 27 Oct draft having taken over
 * from the product what this file used to reword: how often bookings are read
 * (3.7, set per property), the push's checks and
 * backup as protection rather than a guarantee (7.3, stated in the terms'
 * 6.4), the history and the strategy versions as long as the terms keep them
 * (8.1, 8.2), and the Optivo assistant in an article of its own (10).
 */

import type { LegalDoc } from "../legalContent";
import { vopEffectiveDate } from "./effectiveDate";

/**
 * The service's own numbers the terms and the specification state. Each is a
 * constant of the service, named in `specification.parity.test.ts`, which
 * fails when one moves without the other.
 */
export const VOP_FIGURES = {
    /** Days a night's price and lock history is kept after the night (`INITIAL_HISTORY_DAYS`). */
    priceHistoryDays: 30,
    /** Days the upload log and the user activity log are kept (`DEFAULT_RETENTION`). */
    logDays: 21,
    /** Years a card mandate lasts (`RECURRING_MANDATE_YEARS`). */
    mandateYears: 5,
    /** Versions of a strategy kept (`STRATEGY_VERSION_LIMIT`). */
    strategyVersions: 5,
    /** Months a reservation is kept after its stay (`DEFAULT_RETENTION.reservationMonths`). */
    reservationMonths: 24,
    /** Days of the free trial (`TRIAL_DAYS`). */
    trialDays: 30,
    /** Months a promo code extends the trial by (`PROMO_EXTENSION_MONTHS`). */
    trialExtensionMonths: 1,
} as const;

export const VOP_SPECIFICATION_CS: LegalDoc = {
    title: `Specifikace služby`,
    effectiveDate: vopEffectiveDate("cs"),
    intro: [`Příloha č. 1 všeobecných obchodních podmínek.`],
    sections: [
        {
            id: "intro",
            heading: `1. Úvodní ustanovení`,
            clauses: [
                { text: `Tato Specifikace popisuje funkcionality Služby, pravidla výpočtu Vypočtené ceny, zdroje dat, propojení s Rezervačním systémem a požadavky na digitální prostředí Uživatele.` },
                { text: `Specifikace popisuje Službu v plném rozsahu. Které funkcionality jsou součástí jednotlivých Tarifů, stanoví Ceník.` },
                { text: `Specifikace popisuje strukturu výpočtu a jeho vstupy. Konkrétní hodnoty koeficientů a dalších parametrů výpočtu jsou součástí Know-how a obchodním tajemstvím Poskytovatele. Jejich vliv na konkrétní cenu je Uživateli zřejmý z vysvětlení výpočtu Vypočtené ceny.` },
                { text: `Pojmy s velkým počátečním písmenem mají význam stanovený ve VOP.` },
                { text: `Technická dokumentace a nápověda dostupné v Portálu popisují fungování Služby podrobněji, mají informativní povahu a nejsou součástí Specifikace.` },
            ],
        },
        {
            id: "account",
            heading: `2. Účet`,
            clauses: [
                { text: `Služba zajišťuje bezpečné přihlášení k Účtu s odpovídajícím ověřením uživatele.` },
                { text: `K Účtu lze zřídit přístup více osobám.` },
                { text: `Služba umožňuje připojit nástroj Uživatele, včetně agenta umělé inteligence, prostřednictvím osobního přístupového klíče. Rozsah takového přístupu popisuje dokumentace v Portálu.` },
            ],
        },
        {
            id: "connection",
            heading: `3. Propojení s Rezervačním systémem`,
            clauses: [
                { text: `Aktuální seznam podporovaných Rezervačních systémů je uveden v Portálu.` },
                { text: `Propojení provede Uživatel v Portálu zadáním přístupových údajů nebo udělením přístupu podle pravidel příslušného Rezervačního systému. Přístupové údaje Poskytovatel ukládá šifrovaně.` },
                {
                    text: `Služba z Rezervačního systému načítá zejména:`,
                    items: [
                        `typy ubytovacích jednotek a jejich kapacity;`,
                        `kalendář obsazenosti;`,
                        `cenové plány a aktuální ceny;`,
                        `údaje o rezervacích, včetně historických, v rozsahu stanoveném Zpracovatelskou smlouvou.`,
                    ],
                },
                { text: `Jména, kontaktní a identifikační údaje hostů, poznámky k rezervacím a údaje o platebních kartách Služba odstraňuje a neukládá.` },
                {
                    text: `Služba do Rezervačního systému zapisuje zejména:`,
                    items: [
                        `ceny za noc pro základní cenový plán a pro navázané cenové plány;`,
                        `další parametry prodeje, je-li příslušná funkcionalita zapnuta.`,
                    ],
                },
                { text: `Služba zpravidla zapisuje jen hodnoty, které se od posledního Propisu změnily, a jen v rozsahu, který rozhraní Rezervačního systému umožňuje.` },
                { text: `Nové a změněné rezervace Služba v Rezervačním systému zjišťuje pravidelně, zpravidla několikrát denně; interval se může lišit podle nastavení pro Objekt.` },
            ],
        },
        {
            id: "calculation",
            heading: `4. Výpočet ceny`,
            clauses: [
                {
                    text: `Výpočet probíhá podle předem stanovených pravidel s parametry individuálními pro každý Objekt a zahrnuje zejména:`,
                    items: [
                        `stanovení výchozí ceny podle Strategie a historie Objektu;`,
                        `úpravu podle poptávky;`,
                        `úpravu podle státních svátků a školních prázdnin;`,
                        `úpravu podle předpovědi počasí;`,
                        `úpravu podle veřejně nabízených cen v okolí Objektu, je-li zapnuto jejich sledování;`,
                        `denní a sezónní úpravy nastavené Uživatelem;`,
                        `uplatnění Cenových limitů a Zámků.`,
                    ],
                },
                { text: `Vypočtené ceny se přepočítávají pravidelně s četností nastavenou pro Objekt.` },
                { text: `Cíl výpočtu (výnos, obsazenost nebo jejich vyvážení) a reaktivitu volí Uživatel ve Strategii. Výpočet sleduje zvolený cíl; dosažení obchodního výsledku není jeho součástí.` },
                { text: `Cenové limity se uplatní při výpočtu Vypočtené ceny ve všech režimech výpočtu. Cenu opatřenou Zámkem výpočet nepřepisuje.` },
                {
                    text: `Cena propsaná do Rezervačního systému se může od Cenových limitů záměrně odchýlit, zejména:`,
                    items: [
                        `u cen navázaných cenových plánů odvozených od Vypočtené ceny;`,
                        `u cen pro jiný počet hostů, než pro který je Vypočtená cena stanovena;`,
                        `při uplatnění zvláštního minima nastaveného Uživatelem pro prodej na poslední chvíli;`,
                        `u ceny, kterou Uživatel zadal ručně.`,
                    ],
                },
                { text: `Odchylka podle předchozího odstavce není vadou Služby ani Technickou chybou Propisu.` },
                { text: `Služba umožňuje Uživateli zobrazit vysvětlení výpočtu aktuální Vypočtené ceny, zejména vliv jednotlivých zohledněných faktorů.` },
                { text: `Služba využívá automatizované nástroje včetně modelů umělé inteligence k tvorbě Automatizovaných návrhů, zejména klasifikace Objektu a návrhu Strategie. Nenastaví-li Uživatel parametr sám, použije Služba Automatizovaný návrh, který může Uživatel kdykoli změnit.` },
                { text: `Výchozí nastavení, které nabízí průvodce rychlým spuštěním, je obecné. Průvodce nezapíná Automatické odesílání; sledování veřejných cen zapne, jen zvolí-li to v něm Uživatel.` },
            ],
        },
        {
            id: "market-prices",
            heading: `5. Sledování veřejných cen v okolí`,
            clauses: [
                { text: `Funkcionalita je ve výchozím stavu vypnutá; zapíná ji Uživatel.` },
                { text: `Služba zpracovává veřejně zobrazované ceny ubytovacích zařízení v okolí Objektu, které Poskytovatel získává z veřejných zdrojů nebo od třetích osob.` },
                { text: `Do výpočtu vstupuje pouze souhrnná hodnota veřejně nabízených cen vzorku ubytovacích zařízení v okolí Objektu pro danou noc. Služba nereaguje na změnu ceny jednotlivého zařízení ve vzorku.` },
                { text: `Vliv souhrnné hodnoty na Vypočtenou cenu je omezen.` },
                { text: `Služba zobrazuje souhrnné údaje o veřejně nabízených cenách v okolí Objektu. Budoucí ceny, obsazenost ani jiné neveřejné údaje jiných ubytovatelů nezobrazuje ani nepoužívá.` },
                { text: `Ubytovací zařízení jiných uživatelů Služby se ze vzorku nevylučují; i jejich ceny se zpracovávají výlučně v podobě, v jaké jsou veřejně zobrazovány.` },
            ],
        },
        {
            id: "holidays-weather",
            heading: `6. Svátky a počasí`,
            clauses: [
                { text: `Státní svátky a školní prázdniny Služba zjišťuje z veřejných zdrojů.` },
                { text: `Předpověď počasí pro lokalitu Objektu Služba získává z veřejného zdroje a zohledňuje ji v horizontu dostupné předpovědi.` },
            ],
        },
        {
            id: "push",
            heading: `7. Propis a záloha`,
            clauses: [
                { text: `Při Odeslání Uživatel určí rozsah Propisu a Odeslání potvrdí.` },
                { text: `Automatické odesílání je ve výchozím stavu vypnuté. Po aktivaci Služba propisuje Vypočtené ceny v nastaveném rozsahu. Vypnutí je účinné okamžitě.` },
                { text: `Před Propisem Služba ověřuje shodu měny s Rezervačním systémem a ukládá zálohu přepisovaných cen, a to v rozsahu a s omezeními podle VOP.` },
                { text: `Protokol odeslání obsahuje zejména odeslané hodnoty a výsledek Propisu.` },
            ],
        },
        {
            id: "history",
            heading: `8. Historie`,
            clauses: [
                { text: `Služba u každé noci zobrazuje vývoj její ceny včetně zdroje každé změny (výpočet nebo ruční zadání), a to po dobu uchování záznamů podle VOP.` },
                { text: `Služba uchovává předchozí verze Strategie v rozsahu podle VOP; Uživatel se k nim může vrátit.` },
                { text: `Dobu uchování záznamů stanoví VOP.` },
            ],
        },
        {
            id: "analytics",
            heading: `9. Analytika a prognózy`,
            clauses: [
                { text: `Služba může zobrazovat přehledy výkonnosti Objektu a prognózy vypočtené z Dat Uživatele.` },
                { text: `Prognózy mají odhadovou povahu. Jejich odchylka od skutečného vývoje není vadou Služby.` },
                { text: `Služba vytváří přehled strategie, což je automaticky vytvořený přehled nastavení Strategie Objektu s Automatizovanými návrhy jeho úprav, v četnosti podle Ceníku.` },
            ],
        },
        {
            id: "assistant",
            heading: `10. Asistent Optiva`,
            clauses: [
                { text: `Asistent Optiva odpovídá na dotazy Uživatele zadané v Portálu s využitím jazykového modelu. Rozsah jeho užívání stanoví Ceník.` },
                { text: `Odpovědi asistenta jsou generovány automaticky, mají informativní povahu a mohou být nepřesné nebo neúplné; nejsou radou ani doporučením. Asistent nestanoví Vypočtenou cenu a neprovádí Propis.` },
            ],
        },
        {
            id: "support",
            heading: `11. Podpora`,
            clauses: [
                { text: `Podporu Poskytovatel poskytuje prostřednictvím zpráv v Portálu a e-mailem. Na dotazy odpovídá bez zbytečného odkladu.` },
                { text: `Podpora poskytuje součinnost při užívání Služby. Neposuzuje obchodní vhodnost cen ani cenové strategie. Nastavení parametrů na žádost Uživatele se řídí VOP.` },
            ],
        },
        {
            id: "security",
            heading: `12. Zabezpečení`,
            clauses: [
                { text: `Komunikace mezi prohlížečem, Službou a Rezervačním systémem je šifrována. Přístup k datům je oddělen podle jednotlivých uživatelů. Data jsou uložena v datovém centru v Evropské unii.` },
                { text: `Technická a organizační opatření k ochraně osobních údajů stanoví Příloha č. 2.` },
            ],
        },
        {
            id: "requirements",
            heading: `13. Požadavky na digitální prostředí Uživatele`,
            clauses: [
                {
                    text: `Pro řádné fungování Služby Uživatel zajistí:`,
                    items: [
                        `aktuální verzi webového prohlížeče Chrome, Edge, Firefox nebo Safari;`,
                        `stabilní připojení k internetu;`,
                        `prostředky potřebné k bezpečnému ověření uživatele při přihlášení;`,
                        `účet v podporovaném Rezervačním systému s přístupem přes jeho rozhraní, s oprávněním číst rezervace a zapisovat ceny, případně minimální délku pobytu;`,
                        `správně vedené typy ubytovacích jednotek, kapacity, cenové plány a měnu v Rezervačním systému;`,
                        `funkční e-mailovou adresu uvedenou v Účtu.`,
                    ],
                },
            ],
        },
        {
            id: "changes",
            heading: `14. Změny Specifikace`,
            clauses: [
                { text: `Specifikaci lze měnit postupem pro změnu VOP. Rozšíření funkcionalit nebo seznamu podporovaných Rezervačních systémů, které nezhoršuje postavení Uživatele, Poskytovatel oznámí v Portálu.` },
            ],
        },
    ],
};

export const VOP_SPECIFICATION_EN: LegalDoc = {
    title: `Service Specification`,
    effectiveDate: vopEffectiveDate("en"),
    intro: [`Annex No. 1 to the general terms and conditions. This English text is a translation; the Czech version is binding.`],
    sections: [
        {
            id: "intro",
            heading: `1. Introductory provisions`,
            clauses: [
                { text: `This Specification describes the functions of the Service, the rules for calculating a Calculated Price, the data sources, the connection to the Reservation System and the requirements for the User's digital environment.` },
                { text: `The Specification describes the Service in its full scope. Which functions are part of the individual Plans is laid down by the Price List.` },
                { text: `The Specification describes the structure of the calculation and its inputs. The particular values of the coefficients and other calculation parameters form part of the Know-how and are the Provider's trade secret. Their effect on a particular price is apparent to the User from the explanation of the calculation of the Calculated Price.` },
                { text: `Capitalised terms have the meaning given in the GTC.` },
                { text: `The technical documentation and help available in the Portal describe the working of the Service in more detail, are for information only and do not form part of the Specification.` },
            ],
        },
        {
            id: "account",
            heading: `2. The Account`,
            clauses: [
                { text: `The Service provides a secure sign-in to the Account with appropriate verification of the user.` },
                { text: `Access to the Account can be set up for several persons.` },
                { text: `The Service allows a tool of the User, including an artificial intelligence agent, to be connected by means of a personal access key. The scope of such access is described by the documentation in the Portal.` },
            ],
        },
        {
            id: "connection",
            heading: `3. Connection to the Reservation System`,
            clauses: [
                { text: `The current list of supported Reservation Systems is given in the Portal.` },
                { text: `The User makes the connection in the Portal by entering access credentials or by granting access under the rules of the Reservation System concerned. The Provider stores the access credentials encrypted.` },
                {
                    text: `The Service reads from the Reservation System in particular:`,
                    items: [
                        `the types of accommodation units and their capacities;`,
                        `the occupancy calendar;`,
                        `the rate plans and the current prices;`,
                        `booking data, including historical bookings, to the extent set out in the Processing Agreement.`,
                    ],
                },
                { text: `Guests' names, contact and identification data, reservation notes and payment card data are removed by the Service and not stored.` },
                {
                    text: `The Service writes to the Reservation System in particular:`,
                    items: [
                        `nightly prices for the base rate plan and for linked rate plans;`,
                        `other sales parameters, where the functionality concerned is switched on.`,
                    ],
                },
                { text: `The Service as a rule writes only values that have changed since the last Push, and only to the extent the interface of the Reservation System allows.` },
                { text: `The Service checks the Reservation System for new and changed bookings regularly, as a rule several times a day; the interval may differ according to the setting for the Property.` },
            ],
        },
        {
            id: "calculation",
            heading: `4. Calculation of the price`,
            clauses: [
                {
                    text: `The calculation follows pre-set rules with parameters individual to each Property and comprises in particular:`,
                    items: [
                        `setting a starting price according to the Strategy and the history of the Property;`,
                        `an adjustment for demand;`,
                        `an adjustment for public holidays and school holidays;`,
                        `an adjustment for the weather forecast;`,
                        `an adjustment for prices publicly offered in the vicinity of the Property, where their monitoring is switched on;`,
                        `daily and seasonal adjustments set by the User;`,
                        `applying the Price Limits and Locks.`,
                    ],
                },
                { text: `Calculated Prices are recalculated regularly at the frequency set for the Property.` },
                { text: `The goal of the calculation (revenue, occupancy or a balance of the two) and its reactivity are chosen by the User in the Strategy. The calculation follows the goal chosen; achieving a commercial result is not part of it.` },
                { text: `The Price Limits apply in calculating the Calculated Price in every calculation mode. The calculation does not overwrite a price that carries a Lock.` },
                {
                    text: `A price pushed to the Reservation System may intentionally depart from the Price Limits, in particular:`,
                    items: [
                        `for the prices of linked rate plans derived from the Calculated Price;`,
                        `for prices for a different number of guests than the one for which the Calculated Price is set;`,
                        `where a special minimum set by the User for last-minute sales applies;`,
                        `for a price the User entered manually.`,
                    ],
                },
                { text: `A departure under the previous clause is neither a defect of the Service nor a Technical Push Error.` },
                { text: `The Service allows the User to display an explanation of the calculation of the current Calculated Price, in particular the effect of the individual factors taken into account.` },
                { text: `The Service uses automated tools, including artificial intelligence models, to create Automated Proposals, in particular the classification of the Property and a proposal of the Strategy. Where the User does not set a parameter itself, the Service uses the Automated Proposal, which the User may change at any time.` },
                { text: `The default settings offered by the quick-start wizard are general. The wizard does not switch on Automatic Sending; it switches on the monitoring of public prices only if the User chooses so in it.` },
            ],
        },
        {
            id: "market-prices",
            heading: `5. Following public prices in the vicinity`,
            clauses: [
                { text: `The function is switched off by default; the User switches it on.` },
                { text: `The Service processes the publicly displayed prices of accommodation establishments in the vicinity of the Property, which the Provider obtains from public sources or from third parties.` },
                { text: `Only an aggregate value of the prices publicly offered by a sample of accommodation establishments in the vicinity of the Property for the night concerned enters the calculation. The Service does not respond to a change in the price of an individual establishment in the sample.` },
                { text: `The influence of the aggregate value on the Calculated Price is limited.` },
                { text: `The Service displays aggregate data on prices publicly offered in the vicinity of the Property. It neither displays nor uses future prices, occupancy or other non-public data of other accommodation providers.` },
                { text: `Accommodation establishments of other users of the Service are not excluded from the sample; their prices too are processed solely in the form in which they are publicly displayed.` },
            ],
        },
        {
            id: "holidays-weather",
            heading: `6. Holidays and the weather`,
            clauses: [
                { text: `The Service obtains public holidays and school holidays from public sources.` },
                { text: `The Service obtains the weather forecast for the location of the Property from a public source and takes it into account over the horizon of the available forecast.` },
            ],
        },
        {
            id: "push",
            heading: `7. Push and backup`,
            clauses: [
                { text: `On a Sending, the User determines the scope of the Push and confirms the Sending.` },
                { text: `Automatic Sending is switched off by default. Once activated, the Service pushes the Calculated Prices to the extent set. Switching it off takes effect immediately.` },
                { text: `Before a Push, the Service verifies that the currency matches the Reservation System and stores a backup of the prices being overwritten, to the extent and with the limitations set out in the GTC.` },
                { text: `The Sending Log contains in particular the values sent and the result of the Push.` },
            ],
        },
        {
            id: "history",
            heading: `8. History`,
            clauses: [
                { text: `For every night the Service shows the development of its price, including the source of every change (calculation or manual entry), for as long as the records are kept under the GTC.` },
                { text: `The Service keeps previous versions of the Strategy to the extent set out in the GTC; the User may return to them.` },
                { text: `The retention period of the records is laid down by the GTC.` },
            ],
        },
        {
            id: "analytics",
            heading: `9. Analytics and forecasts`,
            clauses: [
                { text: `The Service may display overviews of the performance of the Property and forecasts calculated from the User Data.` },
                { text: `Forecasts are estimates. Their deviation from the actual development is not a defect of the Service.` },
                { text: `The Service creates a strategy overview, which is an automatically created overview of the settings of the Property's Strategy with Automated Proposals for adjusting them, at the frequency set out in the Price List.` },
            ],
        },
        {
            id: "assistant",
            heading: `10. The Optivo assistant`,
            clauses: [
                { text: `The Optivo assistant answers the User's questions entered in the Portal using a language model. The scope of its use is laid down by the Price List.` },
                { text: `The assistant's answers are generated automatically, are for information only and may be inaccurate or incomplete; they are neither advice nor a recommendation. The assistant does not set a Calculated Price and does not carry out a Push.` },
            ],
        },
        {
            id: "support",
            heading: `11. Support`,
            clauses: [
                { text: `The Provider provides support through messages in the Portal and by e-mail. It answers questions without undue delay.` },
                { text: `Support assists with the use of the Service. It does not assess the commercial suitability of prices or pricing strategy. Setting parameters at the User's request is governed by the GTC.` },
            ],
        },
        {
            id: "security",
            heading: `12. Security`,
            clauses: [
                { text: `Communication between the browser, the Service and the Reservation System is encrypted. Access to data is separated by individual user. The data are stored in a data centre in the European Union.` },
                { text: `The technical and organisational measures for the protection of personal data are laid down by Annex 2.` },
            ],
        },
        {
            id: "requirements",
            heading: `13. Requirements for the User's digital environment`,
            clauses: [
                {
                    text: `For the Service to work properly the User ensures:`,
                    items: [
                        `a current version of the Chrome, Edge, Firefox or Safari web browser;`,
                        `a stable internet connection;`,
                        `the means needed to verify the user securely at sign-in;`,
                        `an account in a supported Reservation System with access through its interface, with permission to read reservations and to write prices and, where applicable, the minimum length of stay;`,
                        `correctly kept types of accommodation units, capacities, rate plans and currency in the Reservation System;`,
                        `a working e-mail address stated in the Account.`,
                    ],
                },
            ],
        },
        {
            id: "changes",
            heading: `14. Changes of the Specification`,
            clauses: [
                { text: `The Specification may be changed by the procedure for changing the GTC. An extension of the functions or of the list of supported Reservation Systems that does not worsen the User's position is announced by the Provider in the Portal.` },
            ],
        },
    ],
};
